import { useEffect, useState, useCallback } from 'react';
import { Reward } from '@lunit/shared-types';
import { Coins, Loader2, Gift, Clock, X, Check, AlertCircle, CheckCircle2 } from 'lucide-react';
import {
  getRewards,
  requestRedemption,
  confirmRedemption,
  cancelRedemption,
  getOpenRedemptions,
  getRedemptionStatusById,
  RedemptionRequest,
} from '@core/api/rewards';
import { getRedemptionGateStatus, RedemptionGateStatus } from '@core/api/users';
import { Card } from '@core/components/Card';
import { Button } from '@core/components/Button';
import { Header } from '@core/components/Header';
import { RedemptionGateBanner } from '@core/components/RedemptionGateBanner';
import { useProfile } from '@core/contexts/ProfileContext';
import {
  cancelOutcomeFromError,
  cancelOutcomeFromResult,
  isRouteMissing,
  pointsWord,
  unsupportedCancelOutcome,
} from '@core/utils/redemptionCancel';

// Format time remaining as MM:SS
function formatTimeRemaining(expiresAt: string): string {
  const now = new Date();
  const expires = new Date(expiresAt);
  const diffMs = expires.getTime() - now.getTime();

  if (diffMs <= 0) return '00:00';

  const minutes = Math.floor(diffMs / 60000);
  const seconds = Math.floor((diffMs % 60000) / 1000);

  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

export function RewardsPage() {
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [loading, setLoading] = useState(true);
  const [redeemingId, setRedeemingId] = useState<string | null>(null);
  const [confirmingReward, setConfirmingReward] = useState<Reward | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const { user, loading: profileLoading, refresh } = useProfile();

  // Two-step redemption state
  const [activeRedemption, setActiveRedemption] = useState<RedemptionRequest | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [isExpired, setIsExpired] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmSuccess, setConfirmSuccess] = useState(false);
  const [posOrderStatus, setPosOrderStatus] = useState<'CREATED' | 'ACCEPTED' | 'CANCELLED' | null>(null);
  // Отмена резерва (K-012): шаг подтверждения, запрос в полёте, поддерживает ли бэкенд отмену
  // (null — неизвестно: кнопку показываем, на ошибку «маршрута нет» — прячем).
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelSupported, setCancelSupported] = useState<boolean | null>(null);

  // Redemption gate state
  const [gateStatus, setGateStatus] = useState<RedemptionGateStatus | null>(null);
  const isGateLocked = gateStatus?.gateEnabled === true && gateStatus?.allowed === false;

  // Load rewards (профиль уже загружен ProfileProvider'ом)
  useEffect(() => {
    if (profileLoading) {
      return;
    }
    if (!user) {
      setLoading(false);
      return;
    }

    const loadRewards = async () => {
      try {
        // tierSlug (не имя уровня) — allowedTiers содержит слаги вида 'guest', 'bronze'
        const userTier = user.tierSlug || user.tier || 'bronze';
        const rewardsData = await getRewards(userTier);
        setRewards(rewardsData);

        // Redemption gate status (identity — из сессии; работает и для phone-native)
        try {
          const gate = await getRedemptionGateStatus();
          setGateStatus(gate);
        } catch {
          // Gate check failed — allow redemption by default
        }

        // Открытый резерв (перезапуск мини-аппа/PWA посреди обмена) — вернуть экран кода с «Отменить».
        // Бэкенд без маршрута (404) — отмены нет: кнопку прячем, баллы вернёт экспирация кода.
        try {
          const open = await getOpenRedemptions();
          setCancelSupported(true);
          if (open.length > 0) {
            setActiveRedemption((current) => current ?? open[0]);
            setIsExpired(false);
          }
        } catch (error) {
          if (isRouteMissing(error)) setCancelSupported(false);
        }
      } catch (error) {
        console.error('[RewardsPage] Failed to load:', error);
        try {
          const rewardsData = await getRewards();
          setRewards(rewardsData);
        } catch {
          setRewards([]);
        }
      } finally {
        setLoading(false);
      }
    };

    void loadRewards();
  }, [profileLoading, user?.id]);

  // Timer for active redemption
  useEffect(() => {
    if (!activeRedemption) return;

    const updateTimer = () => {
      const remaining = formatTimeRemaining(activeRedemption.expiresAt);
      setTimeRemaining(remaining);

      if (remaining === '00:00') {
        setIsExpired(true);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [activeRedemption]);

  // Poll for POS auto-confirmation (when QR barcode is shown)
  useEffect(() => {
    if (!activeRedemption?.redemptionId || isExpired || confirmSuccess) return;

    const pollInterval = setInterval(async () => {
      try {
        const status = await getRedemptionStatusById(activeRedemption.redemptionId);
        // Track POS order status from YTimes
        if (status.posOrderStatus !== undefined) {
          setPosOrderStatus(status.posOrderStatus ?? null);
        }
        if (status.status === 'CANCELLED' || status.status === 'EXPIRED') {
          // Отменён кассой (отказ/касса недоступна) или истёк — баллы уже на счёте.
          clearInterval(pollInterval);
          void refresh();
          setActiveRedemption(null);
          setPosOrderStatus(null);
          setConfirmingCancel(false);
          setMessage({
            type: 'error',
            text: status.status === 'CANCELLED'
              ? `Обмен «${activeRedemption.rewardName}» отменён на кассе. Баллы вернулись на счёт.`
              : 'Код истёк. Баллы вернулись на счёт.',
          });
          return;
        }
        if (status.status === 'COMPLETED') {
          setConfirmSuccess(true);
          clearInterval(pollInterval);
          setTimeout(() => {
            setActiveRedemption(null);
            setConfirmSuccess(false);
            setPosOrderStatus(null);
            setMessage({ type: 'success', text: `Награда «${activeRedemption.rewardName}» получена!` });
          }, 2000);
        }
      } catch {
        // Silent — polling failure is not critical
      }
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [activeRedemption, isExpired, confirmSuccess, refresh]);

  // Handle redemption request (Step 1)
  const handleRedeem = useCallback(async (reward: Reward) => {
    console.log('[RewardsPage] handleRedeem called', {
      rewardId: reward.id,
      rewardName: reward.name,
      rewardCost: reward.cost,
      user: user ? { id: user.id, balance: user.balance, tier: user.tier } : null
    });

    if (!user) {
      console.error('[RewardsPage] handleRedeem: USER IS NULL - cannot proceed');
      setMessage({ type: 'error', text: 'Пользователь не загружен. Перезагрузите страницу.' });
      return;
    }

    if (!user.id) {
      console.error('[RewardsPage] handleRedeem: USER.ID IS MISSING', user);
      setMessage({ type: 'error', text: 'ID пользователя не найден.' });
      return;
    }

    try {
      setRedeemingId(reward.id);
      setMessage(null);

      console.log('[RewardsPage] Calling requestRedemption...', {
        rewardId: reward.id,
        userId: user.id
      });

      const result = await requestRedemption(reward.id, user.id);

      console.log('[RewardsPage] requestRedemption SUCCESS', result);

      // Баланс пересчитывает сервер — обновляем профиль
      void refresh();

      // Show the code modal
      setActiveRedemption(result);
      setIsExpired(false);
      setConfirmSuccess(false);
      setPosOrderStatus(null);
      setConfirmingCancel(false);

    } catch (error) {
      console.error('[RewardsPage] requestRedemption FAILED', {
        error,
        errorMessage: error instanceof Error ? error.message : 'unknown',
        errorStack: error instanceof Error ? error.stack : undefined
      });
      const errorMessage = error instanceof Error ? error.message : 'Не удалось получить код';

      // If gate locked error, refresh gate status to show updated banner
      if (errorMessage.includes('Для первого списания') || errorMessage.includes('FIRST_REDEMPTION_LOCKED')) {
        try {
          {
            const gate = await getRedemptionGateStatus();
            setGateStatus(gate);
          }
        } catch {
          // Ignore refresh failure
        }
      }

      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setRedeemingId(null);
    }
  }, [user]);

  // Handle confirmation (Step 2 - when user clicks "Получено")
  const handleConfirm = useCallback(async () => {
    if (!activeRedemption) return;

    try {
      setIsConfirming(true);
      await confirmRedemption(activeRedemption.code);
      setConfirmSuccess(true);

      // Show success for 2 seconds then close
      setTimeout(() => {
        setActiveRedemption(null);
        setConfirmSuccess(false);
        setPosOrderStatus(null);
        setMessage({ type: 'success', text: `Награда «${activeRedemption.rewardName}» получена!` });
      }, 2000);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Ошибка подтверждения';
      setMessage({ type: 'error', text: errorMessage });
      setActiveRedemption(null);
      setPosOrderStatus(null);
    } finally {
      setIsConfirming(false);
    }
  }, [activeRedemption]);

  // «Отменить» / крестик: на бэкенде без отмены — просто закрыть с честным текстом; иначе — шаг подтверждения.
  const handleCancelClick = useCallback(() => {
    if (!activeRedemption) return;
    if (cancelSupported === false) {
      const out = unsupportedCancelOutcome(timeRemaining);
      setActiveRedemption(null);
      setPosOrderStatus(null);
      setMessage({ type: out.tone, text: out.text });
      return;
    }
    setConfirmingCancel(true);
  }, [activeRedemption, cancelSupported, timeRemaining]);

  // Отмена резерва (K-012): сервер возвращает баллы сразу и говорит сколько; баланс — из профиля.
  const handleCancel = useCallback(async () => {
    if (!activeRedemption) return;
    setIsCancelling(true);
    try {
      const result = await cancelRedemption(activeRedemption.redemptionId);
      const out = cancelOutcomeFromResult(result);
      setActiveRedemption(null);
      setPosOrderStatus(null);
      setMessage({ type: out.tone, text: out.text });
    } catch (error) {
      const out = cancelOutcomeFromError(error, timeRemaining);
      if (out.unsupported) setCancelSupported(false);
      if (out.close) {
        setActiveRedemption(null);
        setPosOrderStatus(null);
      }
      setMessage({ type: out.tone, text: out.text });
    } finally {
      setIsCancelling(false);
      setConfirmingCancel(false);
      void refresh();
    }
  }, [activeRedemption, timeRemaining, refresh]);

  // Handle expired - return points
  const handleExpiredClose = useCallback(() => {
    if (!activeRedemption) return;

    void refresh();
    setActiveRedemption(null);
    setPosOrderStatus(null);
  }, [activeRedemption, refresh]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-text-main">
        <Loader2 className="animate-spin" size={32} />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <Header showBack={true} />

      {/* Message */}
      {message && (
        <Card
          className={`px-4 py-3 ${
            message.type === 'success' ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'
          }`}
        >
          {message.text}
        </Card>
      )}

      {/* User Balance */}
      {user && (
        <Card className="bg-gradient-to-r from-primary to-primary/80 text-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm opacity-80">Ваш баланс</div>
              <div className="text-2xl font-bold flex items-center gap-2">
                <Coins className="w-6 h-6" />
                {user.balance}
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm opacity-80">Уровень</div>
              <div className="font-semibold capitalize">{user.tier}</div>
            </div>
          </div>
        </Card>
      )}

      {/* Redemption Gate Banner */}
      {gateStatus && <RedemptionGateBanner gateStatus={gateStatus} />}

      {/* Rewards List */}
      <div className="space-y-3">
        {rewards.map((reward) => (
          <Card key={reward.id}>
            <div className="flex gap-4">
              {reward.imageUrl ? (
                <img
                  src={reward.imageUrl}
                  alt={reward.name}
                  className="w-20 h-20 rounded-2xl object-cover"
                />
              ) : (
                <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <Gift className="w-8 h-8 text-primary" />
                </div>
              )}
              <div className="flex-1">
                <div className="font-semibold text-lg mb-1 text-text-main">
                  {reward.name}
                </div>
                {reward.description && (
                  <div className="text-sm text-text-main opacity-60 mb-2 line-clamp-2">
                    {reward.description}
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 text-primary font-semibold">
                    <Coins className="w-4 h-4" />
                    {reward.cost}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => setConfirmingReward(reward)}
                    disabled={!user || user.balance < reward.cost || redeemingId === reward.id || isGateLocked}
                  >
                    {redeemingId === reward.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Обменять'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        ))}

        {rewards.length === 0 && (
          <Card className="text-center py-8 text-text-main opacity-60">
            <Gift className="w-12 h-12 mx-auto mb-3 opacity-40" />
            Награды скоро появятся
          </Card>
        )}
      </div>

      {/* Confirmation Modal - "Are you at the cashier?" */}
      {confirmingReward && !activeRedemption && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-md bg-black/70">
          <div className="relative w-full max-w-sm overflow-hidden rounded-3xl bg-[#0f1729] border border-[#2a3441] shadow-2xl shadow-black/50 animate-in zoom-in-95">
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-gradient-to-br from-amber-500/20 to-orange-500/10 rounded-full blur-3xl pointer-events-none" />

            <div className="relative p-6 text-center space-y-5">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                <AlertCircle className="w-8 h-8 text-amber-400" />
              </div>

              <h2 className="text-xl font-bold text-white">Обменять награду?</h2>

              <div className="text-base text-white font-medium">
                {confirmingReward.name}
              </div>

              <div className="text-sm text-amber-400/90 bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3">
                Убедитесь, что вы у кассы и бариста отсканировал вашу карту
              </div>

              <div className="flex items-center justify-center gap-2 text-sm text-gray-400">
                <Coins className="w-4 h-4 text-primary" />
                Будет списано {confirmingReward.cost} баллов
              </div>

              <button
                onClick={() => {
                  const reward = confirmingReward;
                  setConfirmingReward(null);
                  handleRedeem(reward);
                }}
                disabled={redeemingId === confirmingReward.id}
                className="w-full py-4 px-6 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 rounded-xl text-white font-semibold text-lg flex items-center justify-center gap-2 shadow-lg shadow-amber-500/30 transition-all duration-200 disabled:opacity-50"
              >
                {redeemingId === confirmingReward.id ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  'Подтвердить'
                )}
              </button>

              <button
                onClick={() => setConfirmingReward(null)}
                className="w-full py-3 text-sm text-gray-500 hover:text-gray-300 transition-colors border border-[#2a3441] rounded-xl hover:bg-[#1a2332]"
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Redemption Code Modal - Dark Theme */}
      {activeRedemption && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-md bg-black/70">
          <div className="relative w-full max-w-sm overflow-hidden rounded-3xl bg-[#0f1729] border border-[#2a3441] shadow-2xl shadow-black/50 animate-in zoom-in-95">
            {/* Decorative glow effects */}
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-gradient-to-br from-amber-500/30 to-orange-500/20 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-gradient-to-tr from-purple-500/20 to-blue-500/20 rounded-full blur-3xl pointer-events-none" />

            {/* Close button */}
            <button
              onClick={isExpired || confirmSuccess ? handleExpiredClose : handleCancelClick}
              aria-label={cancelSupported === false ? 'Закрыть' : 'Отменить обмен'}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 transition-all duration-200"
              disabled={isConfirming || isCancelling}
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>

            {/* Content */}
            <div className="relative p-6 text-center space-y-5">
              {/* Success State */}
              {confirmSuccess ? (
                <div className="py-8">
                  <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center animate-in zoom-in shadow-xl shadow-emerald-500/40">
                    <Check className="w-12 h-12 text-white" strokeWidth={3} />
                  </div>
                  <div className="text-2xl font-bold text-emerald-400">Получено!</div>
                  <div className="text-gray-400 mt-2">Приятного аппетита ☕</div>
                </div>
              ) : isExpired ? (
                /* Expired State */
                <div className="py-6">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-red-500/10 border-2 border-red-500/30 flex items-center justify-center">
                    <AlertCircle className="w-10 h-10 text-red-400" />
                  </div>
                  <div className="text-xl font-bold text-red-400 mb-2">Код истёк</div>
                  <div className="text-sm text-gray-500 mb-6">
                    Баллы возвращены на ваш счёт
                  </div>
                  <button
                    onClick={handleExpiredClose}
                    className="w-full py-3 px-6 bg-[#1a2332] hover:bg-[#252f3f] border border-[#2a3441] rounded-xl text-white font-medium transition-colors"
                  >
                    Закрыть
                  </button>
                </div>
              ) : confirmingCancel ? (
                /* Подтверждение отмены (K-012): баллы вернутся сразу */
                <div className="py-4 space-y-5">
                  <div className="w-16 h-16 mx-auto rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                    <AlertCircle className="w-8 h-8 text-amber-400" />
                  </div>
                  <h2 className="text-xl font-bold text-white">Отменить обмен?</h2>
                  <div className="text-base text-white font-medium">{activeRedemption.rewardName}</div>
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-400">
                    <Coins className="w-4 h-4 text-primary" />
                    {activeRedemption.pointsCost} {pointsWord(activeRedemption.pointsCost)} сразу вернутся на счёт
                  </div>
                  <button
                    onClick={() => void handleCancel()}
                    disabled={isCancelling}
                    className="w-full py-4 px-6 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-400 hover:to-red-500 rounded-xl text-white font-semibold text-lg flex items-center justify-center gap-2 shadow-lg shadow-red-500/30 transition-all duration-200 disabled:opacity-50"
                  >
                    {isCancelling ? <Loader2 className="w-6 h-6 animate-spin" /> : 'Да, отменить'}
                  </button>
                  <button
                    onClick={() => setConfirmingCancel(false)}
                    disabled={isCancelling}
                    className="w-full py-3 text-sm text-gray-500 hover:text-gray-300 transition-colors border border-[#2a3441] rounded-xl hover:bg-[#1a2332]"
                  >
                    Нет, вернуться к коду
                  </button>
                </div>
              ) : (
                /* Active Code State */
                <>
                  {/* Header with icon */}
                  <div className="pt-2">
                    <div className="w-18 h-18 mx-auto mb-4 p-4 rounded-2xl bg-gradient-to-br from-amber-400 via-amber-500 to-orange-500 flex items-center justify-center shadow-xl shadow-amber-500/30">
                      <Gift className="w-10 h-10 text-white" strokeWidth={2} />
                    </div>
                    <h2 className="text-2xl font-bold text-white">Ваша награда</h2>
                  </div>

                  {/* Reward Name */}
                  <div className="text-lg font-semibold text-white">
                    {activeRedemption.rewardName}
                  </div>

                  {/* Timer - pulsing effect when low */}
                  <div className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full border ${
                    timeRemaining <= '01:00'
                      ? 'bg-red-500/10 border-red-500/30 animate-pulse'
                      : 'bg-amber-500/10 border-amber-500/20'
                  }`}>
                    <Clock className={`w-5 h-5 ${timeRemaining <= '01:00' ? 'text-red-400' : 'text-amber-400'}`} />
                    <span className={`font-mono text-xl font-bold ${timeRemaining <= '01:00' ? 'text-red-400' : 'text-amber-400'}`}>
                      {timeRemaining}
                    </span>
                  </div>

                  {/* POS Order Status */}
                  {posOrderStatus && (
                    <div className={`inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full ${
                      posOrderStatus === 'ACCEPTED'
                        ? 'bg-emerald-500/10 text-emerald-400'
                        : posOrderStatus === 'CANCELLED'
                          ? 'bg-red-500/10 text-red-400'
                          : 'bg-amber-500/10 text-amber-400'
                    }`}>
                      {posOrderStatus === 'ACCEPTED' && (
                        <><CheckCircle2 className="w-3.5 h-3.5" /> Заказ принят кассой</>
                      )}
                      {posOrderStatus === 'CREATED' && (
                        <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Передаём на кассу...</>
                      )}
                      {posOrderStatus === 'CANCELLED' && (
                        <><AlertCircle className="w-3.5 h-3.5" /> Касса отклонила заказ</>
                      )}
                    </div>
                  )}

                  {/* Confirm Button - Gradient */}
                  <button
                    onClick={handleConfirm}
                    disabled={isConfirming}
                    className="w-full py-4 px-6 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 rounded-xl text-white font-semibold text-lg flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/30 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isConfirming ? (
                      <Loader2 className="w-6 h-6 animate-spin" />
                    ) : (
                      <>
                        <Check className="w-6 h-6" strokeWidth={2.5} />
                        Получено
                      </>
                    )}
                  </button>

                  {/* Cancel Link */}
                  {/* Отмена резерва: на бэкенде без отмены — «Закрыть» с честным текстом (баллы вернёт истечение кода) */}
                  <button
                    onClick={handleCancelClick}
                    className="w-full py-3 text-sm text-gray-500 hover:text-gray-300 transition-colors border border-[#2a3441] rounded-xl hover:bg-[#1a2332]"
                    disabled={isConfirming || isCancelling}
                  >
                    {cancelSupported === false ? 'Закрыть' : 'Отменить'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
